/** Patient form <-> FHIR Patient, and display helpers. */
import {
  EXT_BIRTHDATE_ESTIMATED,
  EXT_SMS_CONSENT,
  type EthDate,
  type Patient,
  SYS_FACILITY_MRN,
  ethToIso,
  formatEth,
  isoToEth,
  newId,
} from '@assist/core';

export interface PatientForm {
  first: string;
  father: string;
  grandfather: string;
  gender: '' | 'female' | 'male';
  dobMode: 'date' | 'age';
  dob: EthDate | null;
  age: string;
  phone: string;
  mrn: string;
  kebele: string;
  woreda: string;
  /** The patient agreed to SMS visit reminders. */
  smsConsent: boolean;
}

export const emptyForm = (): PatientForm => ({
  first: '',
  father: '',
  grandfather: '',
  gender: '',
  dobMode: 'date',
  dob: null,
  age: '',
  phone: '',
  mrn: '',
  kebele: '',
  woreda: '',
  smsConsent: false,
});

const PHONE = /^(\+251|0)[79]\d{8}$/;
export const normalisePhone = (p: string) => p.replace(/[\s-]/g, '');

export type FormErrors = Partial<Record<keyof PatientForm, string>>;

export function validate(
  f: PatientForm,
  msg: { required: string; badPhone: string; badAge: string },
): FormErrors {
  const e: FormErrors = {};
  if (!f.first.trim()) e.first = msg.required;
  if (!f.father.trim()) e.father = msg.required;
  if (!f.gender) e.gender = msg.required;
  if (f.dobMode === 'date' && !f.dob) e.dob = msg.required;
  if (f.dobMode === 'age') {
    const n = Number(f.age);
    if (f.age.trim() === '' || !Number.isInteger(n) || n < 0 || n > 120) e.age = msg.badAge;
  }
  if (f.phone.trim() && !PHONE.test(normalisePhone(f.phone))) e.phone = msg.badPhone;
  return e;
}

const estimated = (p: Patient) =>
  (p as { extension?: Array<{ url: string; valueBoolean?: boolean }> }).extension?.some(
    (x) => x.url === EXT_BIRTHDATE_ESTIMATED && x.valueBoolean,
  ) ?? false;

/** Build the FHIR Patient. Keeps any fields of `existing` this form does not manage. */
export function toPatient(f: PatientForm, todayIso: string, existing?: Patient): Patient {
  const given = [f.first, f.father, f.grandfather].map((s) => s.trim()).filter(Boolean);
  let birthDate: string | undefined;
  let isEstimated = false;
  if (f.dobMode === 'date' && f.dob) birthDate = ethToIso(f.dob) ?? undefined;
  if (f.dobMode === 'age') {
    birthDate = String(Number(todayIso.slice(0, 4)) - Number(f.age));
    isEstimated = true;
  }
  const base: Record<string, unknown> = { ...(existing ?? {}) };
  const otherExt = (
    (existing as { extension?: Array<{ url: string }> } | undefined)?.extension ?? []
  ).filter((x) => x.url !== EXT_BIRTHDATE_ESTIMATED && x.url !== EXT_SMS_CONSENT);
  const extension: Array<{ url: string; valueBoolean?: boolean }> = [
    ...otherExt,
    ...(isEstimated ? [{ url: EXT_BIRTHDATE_ESTIMATED, valueBoolean: true }] : []),
    // Consent only means something with a phone number to send to.
    ...(f.phone.trim() ? [{ url: EXT_SMS_CONSENT, valueBoolean: f.smsConsent }] : []),
  ];
  const otherIds = (existing?.identifier ?? []).filter((i) => i.system !== SYS_FACILITY_MRN);
  const identifier = f.mrn.trim()
    ? [...otherIds, { system: SYS_FACILITY_MRN, value: f.mrn.trim() }]
    : otherIds;
  const address =
    f.kebele.trim() || f.woreda.trim()
      ? [
          {
            ...(f.kebele.trim() ? { line: [`Kebele ${f.kebele.trim()}`] } : {}),
            ...(f.woreda.trim() ? { district: f.woreda.trim() } : {}),
          },
        ]
      : undefined;
  const p: Record<string, unknown> = {
    ...base,
    resourceType: 'Patient',
    id: existing?.id ?? newId(),
    active: true,
    name: [{ use: 'official', text: given.join(' '), given }],
    gender: f.gender || 'unknown',
  };
  for (const [k, v] of Object.entries({
    birthDate,
    extension: extension.length ? extension : undefined,
    identifier: identifier.length ? identifier : undefined,
    telecom: f.phone.trim() ? [{ system: 'phone', value: normalisePhone(f.phone) }] : undefined,
    address,
  })) {
    if (v === undefined) delete p[k];
    else p[k] = v;
  }
  return p as Patient;
}

export function fromPatient(p: Patient, todayIso: string): PatientForm {
  const given = p.name?.[0]?.given ?? [];
  const est = estimated(p);
  const kebeleLine = p.address?.[0]?.line?.[0] ?? '';
  return {
    first: given[0] ?? '',
    father: given[1] ?? '',
    grandfather: given[2] ?? '',
    gender: p.gender === 'female' || p.gender === 'male' ? p.gender : '',
    dobMode: est || (p.birthDate && p.birthDate.length < 10) ? 'age' : 'date',
    dob: p.birthDate && p.birthDate.length === 10 && !est ? isoToEth(p.birthDate) : null,
    age:
      p.birthDate && (est || p.birthDate.length < 10)
        ? String(Number(todayIso.slice(0, 4)) - Number(p.birthDate.slice(0, 4)))
        : '',
    phone: p.telecom?.find((t) => t.system === 'phone')?.value ?? '',
    mrn: p.identifier?.find((i) => i.system === SYS_FACILITY_MRN)?.value ?? '',
    kebele: kebeleLine.replace(/^Kebele\s+/, ''),
    woreda: p.address?.[0]?.district ?? '',
    smsConsent: smsConsentOf(p),
  };
}

export const smsConsentOf = (p: Patient) =>
  (p as { extension?: Array<{ url: string; valueBoolean?: boolean }> }).extension?.some(
    (x) => x.url === EXT_SMS_CONSENT && x.valueBoolean === true,
  ) ?? false;

export const displayName = (p: Patient) =>
  p.name?.[0]?.text || (p.name?.[0]?.given ?? []).join(' ') || '—';
export const mrnOf = (p: Patient) =>
  p.identifier?.find((i) => i.system === SYS_FACILITY_MRN)?.value;

export interface Age {
  years: number;
  /** Whole months and days of life, when the exact birth date is known (for babies). */
  months?: number;
  days?: number;
  estimated: boolean;
}

/** Age between a FHIR birth date (YYYY or YYYY-MM-DD) and the clinic's today. */
export function ageOf(p: Patient, todayIso: string): Age | null {
  if (!p.birthDate) return null;
  const [ty, tm, td] = todayIso.split('-').map(Number) as [number, number, number];
  const y = Number(p.birthDate.slice(0, 4));
  if (p.birthDate.length < 10) return { years: ty - y, estimated: true };
  const m = Number(p.birthDate.slice(5, 7));
  const d = Number(p.birthDate.slice(8, 10));
  const years = ty - y - (tm < m || (tm === m && td < d) ? 1 : 0);
  const months = (ty - y) * 12 + (tm - m) - (td < d ? 1 : 0);
  const days = Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(y, m - 1, d)) / 86_400_000);
  return { years, months, days, estimated: estimated(p) };
}

export function birthLabel(p: Patient): string | null {
  if (!p.birthDate || p.birthDate.length < 10 || estimated(p)) return null;
  const e = isoToEth(p.birthDate);
  return e ? formatEth(e) : null;
}
